import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    type ReactNode,
} from 'react';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import { GetProfileDocument, type Role, type Gender } from '@/gql/graphql';
import { setToken, removeToken, getToken, registerLogoutCallback } from '@/lib/auth';
import { Sentry } from '@/lib/sentry';

// --- Types ---

interface AuthUser {
    id: string;
    username: string;
    email: string;
    gender: Gender;
    roles: Role[];
    pictureUrl: string | null;
    isProfileComplete: boolean;
    isStravaConnected: boolean;
    isGoogleConnected: boolean;
}

interface AuthContextValue {
    user: AuthUser | null;
    isAuthenticated: boolean;
    isProfileComplete: boolean;
    isLoading: boolean;
    login: (token: string) => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// --- Provider ---

export function AuthProvider({ children }: { children: ReactNode }) {
    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    // loginUser holds the user fetched via login() — separate from query-based user
    const [loginUser, setLoginUser] = useState<AuthUser | null>(null);

    const hasToken = Boolean(getToken());

    const { data, loading, error } = useQuery(GetProfileDocument, {
        skip: !hasToken,
        fetchPolicy: 'network-only',
    });

    const buildUser = useCallback((me: NonNullable<typeof data>['me']): AuthUser => ({
        id: me?.id ?? '',
        username: me?.username ?? '',
        email: me?.email ?? '',
        gender: (me?.gender ?? 'OTHER') as Gender,
        roles: (me?.roles ?? []) as Role[],
        pictureUrl: me?.pictureUrl ?? null,
        isProfileComplete: me?.isProfileComplete ?? false,
        isStravaConnected: me?.isStravaConnected ?? false,
        isGoogleConnected: me?.isGoogleConnected ?? false,
    }), []);

    // Derive user and loading from query state (no setState in effect needed)
    const queryUser = data?.me ? buildUser(data.me) : null;
    const user = loginUser ?? queryUser;
    const isLoading = hasToken && loading && !loginUser;

    // Side-effects only: navigate on auth error, sync Sentry
    useEffect(() => {
        if (!hasToken || loading) return;

        if (error || !data?.me) {
            removeToken();
            Sentry.setUser(null);
            navigate('/login', { replace: true });
            return;
        }

        Sentry.setUser({ id: data.me.id ?? undefined, email: data.me.email ?? undefined, username: data.me.username ?? undefined });
    }, [hasToken, loading, data, error, navigate]);

    // --- Login ---
    const login = useCallback(
        async (token: string) => {
            setToken(token);

            const { data } = await apolloClient.query({
                query: GetProfileDocument,
                fetchPolicy: 'network-only',
            });

            if (data?.me) {
                const u = buildUser(data.me);
                setLoginUser(u);
                Sentry.setUser({ id: data.me.id ?? undefined, email: data.me.email ?? undefined, username: data.me.username ?? undefined });
            }
        },
        [apolloClient, buildUser]
    );

    // --- Logout ---
    const logout = useCallback(async () => {
        removeToken();
        setLoginUser(null);
        Sentry.setUser(null);

        try {
            await apolloClient.clearStore();
        } catch (e) {
            console.error('Error clearing Apollo store:', e);
        }

        navigate('/login', { replace: true });
    }, [apolloClient, navigate]);

    // --- Bridge : ErrorLink Apollo → React ---
    useEffect(() => {
        registerLogoutCallback(() => {
            setLoginUser(null);
            Sentry.setUser(null);
            apolloClient.clearStore().catch(console.error);
            navigate('/login', { replace: true });
        });
    }, [apolloClient, navigate]);

    const value: AuthContextValue = {
        user,
        isAuthenticated: user !== null,
        isProfileComplete: user?.isProfileComplete ?? false,
        isLoading,
        login,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

// --- Hook ---

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return ctx;
}
